import { useCallback, useEffect, useRef, useState } from "react";
import { track } from "@/lib/posthog";

export type NpsTriggerReason =
  | "second_gen"
  | "save_threshold"
  | "retailer_click"
  | "refinement_success"
  | "repeat_refine"
  | "repeat_regen"
  | "shuffle_no_save"
  | "low_activity_dwell"
  | "exit_intent"
  | "error";

interface Signals {
  enabled: boolean;
  designId?: string | null;
  savedCount: number;
  refinementCount: number;
  regenerateCount: number;
  shufflesUsed: number;
  errorCount: number;
}

const DWELL_MS = 90_000;

export function useNpsTriggers(signals: Signals) {
  const {
    enabled,
    designId,
    savedCount,
    refinementCount,
    regenerateCount,
    shufflesUsed,
    errorCount,
  } = signals;

  const [trigger, setTrigger] = useState<NpsTriggerReason | null>(null);
  const firedRef = useRef(false);
  const productClicksRef = useRef(0);
  const prevRefinementCountRef = useRef(refinementCount);

  const fire = useCallback(
    (reason: NpsTriggerReason) => {
      // Always emit trigger_fired for opportunity measurement (even if we swallow it).
      track("nps_trigger_fired", {
        trigger: reason,
        design_id: designId ?? null,
        surface: "mobile_inline",
        suppressed: firedRef.current || !enabled,
      });
      if (!enabled || firedRef.current) return;
      firedRef.current = true;
      setTrigger(reason);
    },
    [designId, enabled],
  );

  // Save threshold
  useEffect(() => {
    if (savedCount >= 2) fire("save_threshold");
  }, [savedCount, fire]);

  // Refinements: first success = positive; ≥2 = friction
  useEffect(() => {
    const prev = prevRefinementCountRef.current;
    prevRefinementCountRef.current = refinementCount;
    if (refinementCount > prev && refinementCount === 1) fire("refinement_success");
    if (refinementCount >= 2) fire("repeat_refine");
  }, [refinementCount, fire]);

  // Repeat regenerate on same design
  useEffect(() => {
    if (regenerateCount >= 2) fire("repeat_regen");
  }, [regenerateCount, fire]);

  // Shuffle without saving
  useEffect(() => {
    if (shufflesUsed >= 2 && savedCount === 0) fire("shuffle_no_save");
  }, [shufflesUsed, savedCount, fire]);

  // Errors
  useEffect(() => {
    if (errorCount > 0) fire("error");
  }, [errorCount, fire]);

  // Dwell timer — 90s with no saves / no product clicks
  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => {
      if (savedCount === 0 && productClicksRef.current === 0) {
        fire("low_activity_dwell");
      }
    }, DWELL_MS);
    return () => clearTimeout(t);
  }, [enabled, savedCount, fire]);

  // Exit intent — page hidden / unload with no engagement
  useEffect(() => {
    if (!enabled) return;
    const onHide = () => {
      if (document.visibilityState !== "hidden") return;
      if (savedCount === 0 && productClicksRef.current === 0) {
        fire("exit_intent");
      }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [enabled, savedCount, fire]);

  const onProductClick = useCallback(() => {
    productClicksRef.current += 1;
    if (productClicksRef.current >= 1) fire("retailer_click");
  }, [fire]);

  return { trigger, onProductClick, fire };
}
